import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import {
  GroupSelectList,
  groupMatchesQuery,
  loadGroupsForDepartment,
  normalizeGroups,
} from "../task/GroupAssignParts";
import { InlineSpinner } from "./inventoryFormShared";

const PANEL_MAX_HEIGHT = 300;

// Group picker for inventory forms. The closed state reads like a normal dropdown;
// the open panel is the same GroupSelectList the task assign / re-assign modals use,
// so groups expand to show their members here too.
// The panel is portaled and fixed-positioned rather than rendered inline: the modal
// body scrolls (overflow-y-auto), which would clip an absolutely positioned menu and
// push the rest of the form down if it were rendered in flow.
// `value` is the group *name*; `onChange(name, group)` also passes the full group so
// callers can send its `groupId` (as `assignedTo`) rather than the name.
export const InventoryGroupPicker = ({
  value,
  onChange,
  langMode,
  open: hostOpen,
  testId = "inventory-group-picker",
}) => {
  const t = translations[langMode] || translations.en;
  const isRtl = isRTL(langMode);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (hostOpen) return undefined;
    setOpen(false);
    setQuery("");
    return undefined;
  }, [hostOpen]);

  useEffect(() => {
    if (!hostOpen) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const raw = await loadGroupsForDepartment();
        if (!cancelled) setGroups(normalizeGroups(raw));
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [hostOpen]);

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
    setPosition({
      left: rect.left,
      width: rect.width,
      top: openUpwards ? undefined : rect.bottom + 6,
      bottom: openUpwards ? window.innerHeight - rect.top + 6 : undefined,
      maxHeight: Math.max(160, Math.min(PANEL_MAX_HEIGHT, openUpwards ? rect.top - 16 : spaceBelow - 16)),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDocDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onReflow = () => place();
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, place]);

  const filteredGroups = useMemo(
    () => groups.filter((group) => groupMatchesQuery(group, query)),
    [groups, query]
  );

  const selectedGroup = useMemo(
    () => groups.find((group) => group.groupName === value) || null,
    [groups, value]
  );

  const handleSelect = (groupId) => {
    if (groupId == null) {
      onChange("", null);
      return;
    }
    const group = groups.find((item) => item.groupId === groupId);
    onChange(group?.groupName ?? "", group ?? null);
    setOpen(false);
  };

  const panel =
    open && position
      ? createPortal(
          <div
            ref={panelRef}
            dir={isRtl ? "rtl" : "ltr"}
            data-testid={`${testId}-panel`}
            style={{
              position: "fixed",
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              maxHeight: position.maxHeight,
              zIndex: 1400,
            }}
            className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
          >
            <div className="relative mb-2 shrink-0">
              <Search
                size={14}
                className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${
                  isRtl ? "right-3" : "left-3"
                }`}
              />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t.inventoryTransferSearchGroup}
                data-testid={`${testId}-search`}
                className={`h-9 w-full rounded-lg border border-slate-200 text-sm outline-none transition focus:border-[#236DB4] focus:ring-2 focus:ring-[#236DB4]/20 ${
                  isRtl ? "pr-8 pl-3" : "pl-8 pr-3"
                }`}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                  <InlineSpinner size={16} />
                  {t.Processing}
                </div>
              ) : (
                <GroupSelectList
                  groups={filteredGroups}
                  selectedGroupId={selectedGroup?.groupId ?? null}
                  onSelectGroup={handleSelect}
                  query={query}
                  langMode={langMode}
                  disabledGroupTooltip={t.groupDisabledNoMembers}
                />
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="w-full">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition hover:border-[#93C5FD] focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20"
      >
        <span className={`min-w-0 truncate ${selectedGroup ? "text-slate-700" : "text-slate-400"}`}>
          {selectedGroup
            ? `${selectedGroup.groupName} (${selectedGroup.members.length})`
            : value || t.inventoryAddGroupAssignment}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {(selectedGroup || value) && (
            <span
              role="button"
              tabIndex={0}
              aria-label={t.clear}
              onClick={(event) => {
                event.stopPropagation();
                onChange("", null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.stopPropagation();
                  onChange("", null);
                }
              }}
              className="rounded p-0.5 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown
            size={16}
            className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>
      {panel}
    </div>
  );
};

export default InventoryGroupPicker;
